/**
 * The marketplace's Charms category — E6 (`design/economy/economy-rebuild.md` §4.4).
 *
 * A charm sells in either currency (four Credits, twelve Gems), so its price glyph is read per
 * row rather than assumed for the whole category. Equip is global (one charm per account, not
 * per loadout slot) — see the header of `shared/src/charms.ts` for why a per-slot value would
 * encode nothing a global one does not already say.
 */
import { confirmAction } from '../ui';
import { formatNumber, t } from '../../i18n';
import { equipCharm, fetchCharmsCatalogue, purchaseCharm, type CharmCatalogueView, type CharmEntry } from '../charms-api';
import { itemArt, tintDot, type MarketItem } from './marketplace-tile';
import { CHARMS, charmArtPath } from '../../../../../shared/src/charms';
import { createMarketSection, type MarketSection } from './marketplace-section';

function hex(value: number): string {
  return `#${value.toString(16).padStart(6, '0')}`;
}

function toItem(entry: CharmEntry, act: (mutate: () => Promise<unknown>) => void, busy: boolean): MarketItem {
  const currencyLabel = entry.currency === 'PREMIUM' ? t('common.gems') : t('common.credits');

  return {
    key: `charm-${entry.id}`,
    category: 'charms',
    name: entry.name,
    description: entry.description,
    // The charm's own picture, with the tint dot as the fallback: the API sends ownership and
    // price, the shared catalogue knows what the thing looks like, and an id this build has
    // never heard of still gets a swatch rather than a hole.
    art: CHARMS[entry.id]
      ? itemArt(charmArtPath(CHARMS[entry.id]!.art), () => tintDot(hex(entry.tint), { glow: entry.glow !== undefined ? hex(entry.glow) : undefined }))
      : tintDot(hex(entry.tint), { glow: entry.glow !== undefined ? hex(entry.glow) : undefined }),
    price: { amount: entry.priceCoins, currency: entry.currency },
    owned: entry.owned,
    equipped: entry.equipped,
    affordable: entry.affordable,
    action: !entry.owned
      ? {
          label: entry.affordable ? t('common.buy') : entry.currency === 'PREMIUM' ? t('shop.needGems') : t('shop.needCredits'),
          variant: entry.affordable ? 'primary' : 'ghost',
          disabled: busy || !entry.affordable,
          onClick: () =>
            void (async () => {
              const confirmed = await confirmAction({
                title: t('confirmDialog.buyTitle'),
                body: t('confirmDialog.buyBody', { item: entry.name, price: `${formatNumber(entry.priceCoins)} ${currencyLabel}` }),
              });
              if (!confirmed) return;
              act(() => purchaseCharm(entry.id));
            })(),
        }
      : !entry.equipped
        ? {
            label: t('common.equip'),
            variant: 'secondary',
            disabled: busy,
            onClick: () =>
              void (async () => {
                const confirmed = await confirmAction({
                  title: t('confirmDialog.equipTitle'),
                  body: t('confirmDialog.equipBody', { item: entry.name }),
                });
                if (!confirmed) return;
                act(() => equipCharm(entry.id));
              })(),
          }
        : {
            label: t('common.remove'),
            variant: 'ghost',
            disabled: busy,
            onClick: () => act(() => equipCharm(0)),
          },
  };
}

export function createCharmsSection(): MarketSection {
  return createMarketSection<CharmCatalogueView>({
    id: 'charms',
    label: t('shop.charms.title'),
    note: t('shop.charms.note'),
    fetchCatalogue: fetchCharmsCatalogue,
    toItems: (catalogue, act, busy) => catalogue.charms.map((entry) => toItem(entry, act, busy)),
  });
}
